/* ------------------------------------------------------------------
   CART DRAWER: reopen at the top, on the card you left.

   Loaded from snippets/right-drawer.liquid -- the file that declares the
   scroll container this works on -- behind the same `settings.cart_type ==
   'drawer' and template.name != 'cart'` guard that layout/theme.liquid uses
   to decide whether to render snippets/drawer-cart.liquid, so it exists
   exactly where the drawer exists and nowhere else.

   Two positions are written when the drawer is reopened after being closed,
   and they are deliberately NOT treated the same way:

     1. the drawer's own vertical scroll -- #right-drawer-slot, the element
        snippets/right-drawer.liquid gives overflow-y-auto and teleports the
        cart into. It is the scroll container for the whole panel; the
        header is sticky to it and the total/buttons block is sticky to it,
        which is why neither moves while the line items travel. This always
        goes back to the top.

     2. the "YOU MAY ALSO CONSIDER" strip -- [data-cart-drawer-recs-track]
        in snippets/drawer-cart.liquid, a real overflow-x scroller with
        scroll-snap, so its position is a scrollLeft and not a transform.
        This goes back to the CARD the customer left it on.

   WHY EITHER OF THEM HAS TO BE WRITTEN AT ALL, rather than left to the
   browser. Chrome PRESERVES a scroller's offset across display: none and
   restores it when the box comes back -- measured here: a box scrolled to
   300, hidden, written back to 0 while hidden, then shown, reads 300 again.
   It also means a write made while the drawer is hidden is thrown away: an
   element with display: none has no scrolling box, so the scrollTop setter
   has nothing to act on and the saved offset wins. The theme's own close
   events are no help for the same reason -- `cart-is-closed` is dispatched
   from _x_doHide AFTER that function has set display: none, so by the time
   it fires there is nothing left to write to. The one moment early enough,
   `cart-will-close`, is with the drawer still fully on screen, and moving
   anything there is precisely the visible snap this is meant to avoid.

   That restore is also not something to lean on for the carousel: it is a
   raw pixel offset, it is Chrome-specific, and it means nothing once the
   strip has been re-rendered with different products.

   SO BOTH POSITIONS ARE WRITTEN ON THE WAY IN, in the one frame where the
   panel has a box again but has not been painted in place yet.

   Alpine's x-show calls el._x_doShow() to take the inline display: none off
   the slot, and it calls it from the transition's `before` stage -- one frame
   BEFORE the stage that removes `translate-x-full` and starts the slide. So
   at the instant _x_doShow returns, the slot is laid out (scrollTop is
   writable and the upsell track has a width) and is still parked completely
   off the right edge of the viewport. Writing both positions there cannot be
   seen: the first frame the customer ever sees of the panel already has them
   where they belong.

   _x_doShow is the theme's own extension point, not a private hook -- the
   modals store in assets/global.bundle.js assigns slotEl._x_doShow and
   slotEl._x_doHide for all six modal slots in exactly this way to hang its
   `-is-open` / `-is-closed` events off them. This wraps the function the
   store installed rather than replacing it, so those events still fire
   normally.

   THE CARD IS REMEMBERED BY PRODUCT, NOT BY PIXEL OR BY POSITION IN THE ROW.

   What is stored on close is the product id of the card sitting at the left
   edge of the track -- the one scroll-snap-align: start has parked there --
   found by comparing each card's own offset inside the track against the
   current scrollLeft and taking the nearest. What is written back on open is
   that card's offset, measured again from the layout that exists at that
   moment. So the track always lands exactly on a snap point, and it lands on
   the right card even if the strip has been re-rendered in a different order
   or at a different card width: nothing about the old geometry is carried
   over, only which product it was.

   A product id rather than an index because the row is not stable. The strip
   is Shopify Search & Discovery's complementary products for the first item
   in the cart, and the script at the foot of snippets/drawer-cart.liquid
   drops any card whose product is already in the cart -- so card 3 after a
   cart change is not necessarily the card 3 the customer was looking at. If
   the remembered product is no longer in the strip, the track goes to the
   first card, which is the same place it went before this was added.

   Nothing is written if the strip has not rendered yet. On the first open of
   a page load the cards arrive asynchronously, from the recommendations
   endpoint, after the panel is already on screen; there is nothing to
   restore into and no memory to restore from. The memory is a variable in
   this closure, so it lasts as long as the page does and is gone on the next
   load -- which is the same lifetime as the strip it describes.

   WHAT ARMS IT, and why "the drawer is opening" is not on its own enough.
   snippets/drawer-cart.liquid opens the drawer on add-to-cart through
   ModalCart, which runs closeAll() and then open('cart'). With the drawer
   already open that pair leaves the store's `open` flag at true across the
   effect flush, so Alpine sees no change and the panel never moves -- but a
   full set of will-close / will-open events is dispatched anyway. Acting on
   the event alone would therefore yank a customer who is reading the upsell
   strip back to the top of a drawer that never closed.

   The test that separates the two is whether the panel currently occupies
   any space: getClientRects() is empty only when the slot (or an ancestor)
   is display: none, which is to say only when the drawer is genuinely shut.
   That is read once, on `theme:cart-drawer:opening`, which the theme
   dispatches from `cart-will-open` -- before the store sets open = true and
   so before anything has started to move. A reopen from closed arms;
   anything happening inside an open drawer does not, and a quantity change
   or an accordion does not raise the event at all.

   A reopen that interrupts the 300ms close transition is deliberately not
   armed either: the panel still has its box, so it was never actually shut,
   and it is on screen sliding out -- the same visible jump as writing on
   close.

   Everything is guarded. If theme-events.js is not present, if the slot has
   not been teleported yet, or if a future Alpine drops _x_doShow, this
   installs nothing and the drawer behaves exactly as it does today.
   ------------------------------------------------------------------ */

(function () {
  var SLOT_ID = 'right-drawer-slot';
  var TRACK_SELECTOR = '[data-cart-drawer-recs-track]';
  var CARD_SELECTOR = '[data-rec-product-id]';
  var CARD_ID_ATTRIBUTE = 'data-rec-product-id';
  var WRAPPED_FLAG = '__cartDrawerScrollResetWrapped';

  /* The `accordion_group` snippets/drawer-cart.liquid passes to every
     `collapsible` row it renders. The two must stay in step: change the group
     there and this stops matching, silently, and the rows simply keep their
     open state across a reopen. */
  var ACCORDION_GROUP = 'cart-drawer';

  /* Any id no row can have. `collapsible` closes every row in the group whose
     id is not the sender's, so an id belonging to nobody closes all of them. */
  var ACCORDION_RESET_ID = '__cart-drawer-reset__';

  /* Set only by a genuine closed -> open, and cleared by the write itself, so
     one arming can never spend itself twice. */
  var armed = false;

  /* The product on the card the customer left at the left edge of the strip.
     Null until the drawer has been closed at least once with cards showing. */
  var rememberedCardId = null;

  function hasLayoutBox(el) {
    return !!el && el.getClientRects().length > 0;
  }

  /* A card's own scroll offset inside the track. Taken from the two rects
     rather than from offsetLeft, which is measured against whatever the
     nearest positioned ancestor happens to be and so would not survive a
     change of position on any wrapper between them. */
  function cardOffset(track, card) {
    return (
      card.getBoundingClientRect().left -
      track.getBoundingClientRect().left +
      track.scrollLeft
    );
  }

  function cards(track) {
    return track.querySelectorAll(CARD_SELECTOR);
  }

  function rememberCard(slot) {
    var track = slot.querySelector(TRACK_SELECTOR);
    if (!track) return;

    var list = cards(track);
    if (!list.length) return;

    /* Nearest card to the current offset is the one parked at the left edge.
       At the far end of the track the last cards cannot all reach that edge,
       so the nearest is the closest thing to it -- and writing its offset
       back clamps to the same place, which is the same view. */
    var best = null;
    var bestDistance = Infinity;

    for (var i = 0; i < list.length; i++) {
      var distance = Math.abs(cardOffset(track, list[i]) - track.scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = list[i];
      }
    }

    if (best) rememberedCardId = best.getAttribute(CARD_ID_ATTRIBUTE);
  }

  function restoreCard(track) {
    var list = cards(track);

    /* No strip yet -- leave the track alone rather than writing a zero into
       something that is about to be filled in. */
    if (!list.length) return;

    var target = null;

    if (rememberedCardId) {
      for (var i = 0; i < list.length; i++) {
        if (list[i].getAttribute(CARD_ID_ATTRIBUTE) === rememberedCardId) {
          target = list[i];
          break;
        }
      }
    }

    /* No memory, or the remembered product is not in this strip any more:
       the first card, exactly as before. */
    var offset = target ? cardOffset(track, target) : 0;

    if (track.scrollLeft !== offset) track.scrollLeft = offset;
  }

  /* Collapse every accordion row in the drawer.

     This does not reach into the rows. `collapsible` already coordinates a
     group over a `collapsible-open` event on window -- the row you open sends
     one, and every other row in the same group closes on hearing it. Sending
     that event with an id no row owns is simply that contract used with no
     sender, so all of them close. Nothing new is introduced, and the rows keep
     their own animation, chevrons and click behaviour untouched.

     Called once the drawer is fully closed rather than while it is closing.
     x-collapse animates the panel shut over ~300ms, and at `closing` the
     drawer is still on screen for about as long -- the customer would watch
     the row fold up as the panel slid away. After `closed` the panel is
     display: none, so the same animation runs where nothing can see it and
     the drawer is already collapsed by the time it is reopened. */
  function collapseAccordions() {
    window.dispatchEvent(
      new CustomEvent('collapsible-open', {
        detail: { group: ACCORDION_GROUP, id: ACCORDION_RESET_ID }
      })
    );
  }

  function applyOpeningPositions(slot) {
    /* Read before writing: an unchanged scroller is left completely alone,
       so nothing here can disturb a position that is already correct. */
    if (slot.scrollTop !== 0) slot.scrollTop = 0;

    var track = slot.querySelector(TRACK_SELECTOR);
    if (track) restoreCard(track);
  }

  function wrapDoShow(slot) {
    if (slot[WRAPPED_FLAG]) return;

    var original = slot._x_doShow;
    if (typeof original !== 'function') return;

    slot[WRAPPED_FLAG] = true;

    slot._x_doShow = function () {
      var result = original.apply(this, arguments);

      /* original() has already taken display: none off, synchronously, so the
         slot is measurable from here -- and the enter transition has not
         started, so it is still translated a full width off screen. */
      if (armed) {
        armed = false;
        applyOpeningPositions(slot);
      }

      return result;
    };
  }

  document.addEventListener('theme:cart-drawer:opening', function () {
    var slot = document.getElementById(SLOT_ID);
    if (!slot) return;

    wrapDoShow(slot);

    /* No box means the drawer is shut, which means this is a fresh reopen. */
    armed = !hasLayoutBox(slot);
  });

  /* The mirror of the event above, dispatched from cart-will-close while the
     drawer is still open and measurable. Reading here rather than on a scroll
     listener keeps this to the two moments the theme already announces. */
  document.addEventListener('theme:cart-drawer:closing', function () {
    var slot = document.getElementById(SLOT_ID);
    if (slot) rememberCard(slot);
  });

  /* Separate listener, and deliberately not folded into the one above: the
     card has to be read while the drawer is still measurable, and the rows
     have to be collapsed after it is not. Two moments, two handlers. The
     vertical reset and the carousel memory above are untouched by this. */
  document.addEventListener('theme:cart-drawer:closed', collapseAccordions);
})();
